type DomRemovalDiagnostic = {
  expectedParent: string;
  child: string;
  actualParent: string;
};

const diagnosticByError = new WeakMap<object, DomRemovalDiagnostic>();
let installations = 0;
let originalRemoveChild: typeof Node.prototype.removeChild | undefined;
let installedRemoveChild: typeof Node.prototype.removeChild | undefined;

// Keep this temporary diagnostic free of text, URLs, IDs, classes, and other user data.
function describeNode(node: Node | null): string {
  if (!node) return 'null';

  const path: string[] = [];
  for (let current: Node | null = node; current && path.length < 5; current = current.parentNode) {
    if (current.nodeType === Node.ELEMENT_NODE) {
      const element = current as Element;
      const marker = element.getAttribute('data-srp-component');
      const safeMarker = marker && /^[a-z-]{1,40}$/.test(marker) ? `[data-srp-component=${marker}]` : '';
      path.push(`${element.localName}${safeMarker}`);
    } else {
      path.push(current.nodeType === Node.TEXT_NODE ? '#text' : `node-type-${current.nodeType}`);
    }
  }

  return path.join(' < ');
}

export function installDomRemovalDiagnostics(): () => void {
  if (installations++ === 0) {
    const nativeRemoveChild = Node.prototype.removeChild;
    originalRemoveChild = nativeRemoveChild;
    const diagnosticRemoveChild = function removeChild<T extends Node>(this: Node, child: T): T {
      try {
        return nativeRemoveChild.call(this, child) as T;
      } catch (error) {
        if (error && typeof error === 'object' && 'name' in error && error.name === 'NotFoundError') {
          try {
            diagnosticByError.set(error, {
              expectedParent: describeNode(this),
              child: describeNode(child),
              actualParent: describeNode(child.parentNode),
            });
          } catch {
            // Diagnostics must never replace the original DOM exception.
          }
        }
        throw error;
      }
    };
    Node.prototype.removeChild = diagnosticRemoveChild;
    installedRemoveChild = diagnosticRemoveChild;
  }

  let restored = false;
  return () => {
    if (restored) return;
    restored = true;
    if (--installations === 0 && originalRemoveChild) {
      if (Node.prototype.removeChild === installedRemoveChild) {
        Node.prototype.removeChild = originalRemoveChild;
      }
      originalRemoveChild = undefined;
      installedRemoveChild = undefined;
    }
  };
}

export function getDomRemovalDiagnostic(error: Error): DomRemovalDiagnostic | undefined {
  return diagnosticByError.get(error);
}
